/**
 * Classic Premium Deposit Screen UI
 * Fully upgraded UI ONLY — Functionality remains same
 */

import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  StatusBar,
  TouchableOpacity,
  ScrollView,
  TextInput,
  Image,
  Alert,
  ActivityIndicator,
  Platform,
  Clipboard,
  Modal,
} from 'react-native';
import Icon from '../components/Icon';
import { launchImageLibrary } from 'react-native-image-picker';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useTheme } from '../context/ThemeContext';
import { api, BASE_URL, announcementsAPI } from '../services/api';

const DepositScreen = ({ navigation, route }) => {
  const { colors, isDarkMode } = useTheme();

  const [selectedMethod, setSelectedMethod] = useState(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);
  const [amount, setAmount] = useState('');
  const [upiId, setUpiId] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [paymentScreenshot, setPaymentScreenshot] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [showSubmissions, setShowSubmissions] = useState(false);
  const [authStatus, setAuthStatus] = useState('checking');
  const [planDetails, setPlanDetails] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalData, setSuccessModalData] = useState(null);

  // Check auth status on component mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const token = await AsyncStorage.getItem('authToken');
        if (!token) {
          setAuthStatus('no-token');
          return;
        }
        
        // Test token validity
        const testResponse = await api.get('/user/submissions');
        setAuthStatus('valid');
        console.log('Auth status: Token is valid');
      } catch (error) {
        console.log('Auth status check failed:', error.response?.status);
        if (error.response?.status === 401) {
          setAuthStatus('expired');
          await AsyncStorage.removeItem('authToken');
        } else {
          setAuthStatus('network-error');
        }
      }
    };
    
    checkAuthStatus();
  }, []);

  // Fetch QR code when selectedMethod changes or route params change
  useEffect(() => {
    // Handle selectedMethod from route params (if navigated with specific method)
    const routeSelectedMethod = route?.params?.selectedMethod;
    if (routeSelectedMethod && !selectedMethod) {
      fetchQRCodeForMethod(routeSelectedMethod);
    } else if (selectedMethod) {
      // Map display name to API title
      let apiTitle = '';
      if (selectedMethod.name === 'USDT (TRC20)') {
        apiTitle = 'USDT-TRC20';
      } else if (selectedMethod.name === 'USDT (BEP20)') {
        apiTitle = 'USDT-BEP20';
      } else if (selectedMethod.name === 'Perfect Money') {
        apiTitle = 'Perfect Money';
      }
      
      if (apiTitle) {
        fetchQRCodeForMethod(apiTitle);
      }
    }
  }, [selectedMethod, route?.params?.selectedMethod]);

  // Deposit list
  const depositMethods = [
    {
      id: '1',
      name: 'USDT (TRC20)',
      color: '#00E676',
      commissionValue: 'Network free only',
      avgTimeValue: '10 - 15 mins',
      limitsValue: 'No limits',
    },
    {
      id: '2',
      name: 'USDT (BEP20)',
      color: '#00E676',
      commissionValue: 'Network free only',
      avgTimeValue: '10 - 15 mins',
      limitsValue: 'No limits',
    },
    {
      id: '3',
      name: 'Perfect Money',
      color: '#FF5252',
      commissionValue: '0%',
      avgTimeValue: 'Instant',
      limitsValue: 'Limit ₹ 49000/-',
    }
  ];

  // Fetch Submissions
  const fetchSubmissions = async () => {
    setLoadingSubmissions(true);
    try {
      const response = await api.get('/user/submissions');
      setSubmissions(response || []);
    } catch (error) {
      console.error('Error fetching submissions:', error);
      Alert.alert('Error', 'Failed to load submissions');
    } finally {
      setLoadingSubmissions(false);
    }
  };

  useEffect(() => {
    fetchSubmissions();
    
    // Handle navigation from subscription plan
    const { prefilledAmount, planId, planTitle, planCommission, selectedMethod: routeSelectedMethod } = route?.params || {};
    
    if (prefilledAmount) {
      setAmount(prefilledAmount);
      
      // If coming from a subscription plan, setup payment details automatically
      if (planId) {
        // Set plan details in state for display
        setPlanDetails({
          id: planId,
          title: planTitle,
          commission: planCommission
        });
        
        // Auto-select first method and show payment details
        if (depositMethods.length > 0) {
          const firstMethod = depositMethods[0];
          setSelectedMethod(firstMethod);
          
          // Set UPI ID
          const upiIdToUse = 'merchant@paytm';
          setUpiId(upiIdToUse);
          
          // Map method name to API title and fetch QR
          let apiTitle = '';
          if (firstMethod.name === 'USDT (TRC20)') {
            apiTitle = 'USDT-TRC20';
          } else if (firstMethod.name === 'USDT (BEP20)') {
            apiTitle = 'USDT-BEP20';
          } else if (firstMethod.name === 'Perfect Money') {
            apiTitle = 'Perfect Money';
          }
          
          if (apiTitle) {
            fetchQRCodeForMethod(apiTitle);
          }
          
          setShowPaymentForm(true);
          setShowPaymentDetails(true);
        }
      }
      // Regular pre-filled amount (not from plan)
      else if (depositMethods.length > 0) {
        const firstMethod = depositMethods[0];
        setSelectedMethod(firstMethod);
        
        // Set UPI ID
        const upiIdToUse = 'merchant@paytm';
        setUpiId(upiIdToUse);
        
        // Map method name to API title and fetch QR
        let apiTitle = '';
        if (firstMethod.name === 'USDT (TRC20)') {
          apiTitle = 'USDT-TRC20';
        } else if (firstMethod.name === 'USDT (BEP20)') {
          apiTitle = 'USDT-BEP20';
        } else if (firstMethod.name === 'Perfect Money') {
          apiTitle = 'Perfect Money';
        }
        
        if (apiTitle) {
          fetchQRCodeForMethod(apiTitle);
        }
        
        setShowPaymentForm(true);
        setShowPaymentDetails(true);
      }
    }
    
    // Handle direct selectedMethod from route params
    if (routeSelectedMethod && !prefilledAmount) {
      fetchQRCodeForMethod(routeSelectedMethod);
    }
  }, [route?.params]);

  // Method Select - Go directly to payment details
  const handleMethodSelect = (method) => {
    // Map display names to API titles
    let apiTitle = '';
    if (method.name === 'USDT (TRC20)') {
      apiTitle = 'USDT-TRC20';
    } else if (method.name === 'USDT (BEP20)') {
      apiTitle = 'USDT-BEP20';
    } else if (method.name === 'Perfect Money') {
      apiTitle = 'Perfect Money';
    }
    
    setSelectedMethod(method);
    
    // If amount is not pre-filled, clear it for manual entry
    if (!route?.params?.prefilledAmount) {
      setAmount('');
    }
    
    // Set UPI ID
    const upiIdToUse = 'merchant@paytm';
    setUpiId(upiIdToUse);
    
    // Store the API title for fetching QR
    if (apiTitle) {
      fetchQRCodeForMethod(apiTitle);
    }
    
    setShowPaymentForm(true);
    setShowPaymentDetails(true);
  };

  // Generate Payment Details
  const handleGeneratePaymentDetails = () => {
    if (!amount || parseFloat(amount) <= 0) {
      Alert.alert('Error', 'Enter valid amount');
      return;
    }

    setShowPaymentDetails(true);
  };

  // Fetch QR Code based on selected method
  const fetchQRCodeForMethod = async (apiTitle) => {
    console.log('🔍 Starting fetchQRCodeForMethod with apiTitle:', apiTitle);
    
    try {
      if (apiTitle) {
        // Get auth token
        const token = await AsyncStorage.getItem('authToken');
        console.log('🔑 Auth token found:', token ? 'Yes' : 'No');
        
        const apiUrl = `https://a77.onetradebot.com/api/announcements/search/by-title?title=${encodeURIComponent(apiTitle)}`;
        console.log('📡 API URL:', apiUrl);
        
        const headers = {
          'Content-Type': 'application/json',
        };
        
        // Add authorization header if token exists
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
          console.log('🔑 Authorization header added');
        } else {
          console.log('⚠️ No auth token found, making request without authorization');
        }
        
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: headers
        });
        console.log('📡 Response status:', response.status);
        
        const data = await response.json();
        console.log('📦 API Response data:', JSON.stringify(data, null, 2));
        
        if (data.success && data.data && data.data.length > 0) {
          console.log('✅ Data found, processing announcement item...');
          const item = data.data[0];
          console.log('📦 Item:', item);

          // If API provides a description field (used here to carry UPI ID), use it
          if (item.description) {
            console.log('🔔 Setting UPI ID from API description:', item.description);
            setUpiId(item.description);
          }

          const imageField = item.image;
          console.log('🖼️ Image field:', imageField);

          if (imageField) {
            const qrCodeUrl = imageField.startsWith('http') ? imageField : `https://a77.onetradebot.com/${imageField}`;
            console.log('🎯 Final QR URL:', qrCodeUrl);
            setQrCode(qrCodeUrl);
            console.log('✅ QR Code set successfully from API:', qrCodeUrl);
            return;
          } else {
            console.log('❌ No image field found in response');
          }
        } else {
          console.log('❌ No data found in API response or data array is empty');
          console.log('Success:', data.success);
          console.log('Data array length:', data.data ? data.data.length : 'data is null/undefined');
        }
      } else {
        console.log('❌ No apiTitle provided');
      }
    } catch (error) {
      console.error('❌ Error fetching QR code:', error);
    }
    
    // Fallback to generated QR (use API-provided UPI if available, otherwise keep existing fallback)
    console.log('🔄 Using fallback QR code generation...');
    const currentAmount = amount || '0';
    const upiIdToUse = upiId || 'merchant@paytm';
    const fallbackUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=upi://pay?pa=${encodeURIComponent(upiIdToUse)}&am=${encodeURIComponent(currentAmount)}&cu=USD`;
    console.log('🔄 Fallback QR URL:', fallbackUrl);
    setQrCode(fallbackUrl);
  };

  // Upload Screenshot Picker
  const handleSelectPaymentScreenshot = () => {
    launchImageLibrary({ mediaType: 'photo' }, (response) => {
      if (response.assets) setPaymentScreenshot(response.assets[0]);
    });
  };

  const handleUploadPaymentProof = async () => {
    if (!paymentScreenshot) {
      Alert.alert('Error', 'Please upload payment screenshot');
      return;
    }

    if (!transactionId.trim()) {
      Alert.alert('Error', 'Please enter transaction ID');
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      Alert.alert('Error', 'Please enter a valid amount');
      return;
    }

    setIsLoading(true);

    try {
      const token = await AsyncStorage.getItem('authToken');
      if (!token) {
        Alert.alert('Authentication Required', 'Please login to upload payment proof.');
        setIsLoading(false);
        return;
      }

      // Step 1: Create initial submission with screenshot
      const submissionFormData = new FormData();
      const depositAmount = parseFloat(amount);
      const formattedAmount = depositAmount.toFixed(2);
      
      const submissionText = `DEPOSIT REQUEST - AWAITING APPROVAL
===================
Amount: $${formattedAmount}
Payment Method: ${selectedMethod?.name || 'UPI'}
Status: Pending transaction details submission
===================`;
      
      submissionFormData.append('text', submissionText);
      submissionFormData.append('type', 'DEPOSIT');
      
      // Add image
      const fileUri = paymentScreenshot.uri;
      const fileName = paymentScreenshot.fileName || `deposit_${Date.now()}.jpg`;
      const fileType = paymentScreenshot.type || 'image/jpeg';
      
      submissionFormData.append('image', {
        uri: fileUri,
        type: fileType,
        name: fileName,
      });

      console.log('Step 1: Creating submission with screenshot...');
      const submissionResponse = await fetch(BASE_URL + '/user/submissions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: submissionFormData,
      });

      if (!submissionResponse.ok) {
        const errorText = await submissionResponse.text();
        throw new Error(`Failed to create submission: ${errorText}`);
      }

      const submissionResult = await submissionResponse.json();
      console.log('Submission created successfully:', submissionResult);

      // Step 2: Create reply with transaction details and amount for admin approval
      const replyFormData = new FormData();
      replyFormData.append('transactionId', transactionId.trim());
      replyFormData.append('amount', formattedAmount);
      
      // Add image to reply as well for admin reference
      replyFormData.append('image', {
        uri: fileUri,
        type: fileType,
        name: fileName,
      });

      console.log('Step 2: Creating reply with transaction details for admin approval...');
      console.log('Reply data:', {
        submissionId: submissionResult._id,
        transactionId: transactionId.trim(),
        amount: formattedAmount
      });

      const replyResponse = await fetch(`${BASE_URL}/user/submissions/${submissionResult._id}/reply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: replyFormData,
      });

      if (!replyResponse.ok) {
        const errorText = await replyResponse.text();
        throw new Error(`Failed to create reply: ${errorText}`);
      }

      const replyResult = await replyResponse.json();
      console.log('Reply created successfully:', replyResult);

      // Refresh submissions list
      await fetchSubmissions();

      // Show custom success modal
      setSuccessModalData({
        amount: formattedAmount,
        transactionId: transactionId.trim(),
        paymentMethod: selectedMethod?.name || 'UPI'
      });
      setShowSuccessModal(true);

    } catch (error) {
      console.error('Upload error:', error);
      
      let errorMessage = 'Failed to upload payment proof. Please try again.';
      if (error.message.includes('Failed to create submission')) {
        errorMessage = 'Failed to upload screenshot. Please check your internet connection and try again.';
      } else if (error.message.includes('Failed to create reply')) {
        errorMessage = 'Screenshot uploaded but failed to submit transaction details. Please try adding details manually from your submissions.';
      }
      
      Alert.alert('Upload Failed', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Reply to Submission with Transaction Details and Screenshot
  const handleReplyToSubmission = async (submissionId, transactionId, amount, imageUri = null) => {
    try {
      const token = await AsyncStorage.getItem('authToken');
      if (!token) {
        Alert.alert('Authentication Required', 'Please login to reply to submission.');
        return;
      }

      // Validate required parameters
      if (!submissionId || !transactionId || !amount) {
        Alert.alert('Error', 'Missing required information for submission reply.');
        return;
      }

      // Use FormData for file upload
      const formData = new FormData();
      formData.append('transactionId', String(transactionId));
      formData.append('amount', parseFloat(amount).toString());
      formData.append('paymentMethod', selectedMethod?.name || 'UPI');
      
      // Add image if provided
      if (imageUri) {
        const fileName = `transaction_${Date.now()}.jpg`;
        formData.append('image', {
          uri: imageUri,
          type: 'image/jpeg',
          name: fileName,
        });
      }

      console.log('Submitting reply with FormData for submission:', submissionId);
      
      // Use fetch with FormData for file upload
      const response = await fetch(`${BASE_URL}/user/submissions/${submissionId}/reply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          // Don't set Content-Type for FormData, let browser set it with boundary
        },
        body: formData
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      console.log('Reply submitted successfully:', result);
      
      Alert.alert(
        '✅ Reply Submitted',
        `Transaction details submitted successfully!\n\nTransaction ID: ${transactionId}\nAmount: $${amount}\n\nAdmin will review and approve. Amount will be credited to your wallet after approval.`,
        [{ text: 'OK', onPress: () => fetchSubmissions() }]
      );
    } catch (error) {
      console.error('Error replying to submission:', error);
      Alert.alert('Error', 'Failed to submit reply. Please try again.');
    }
  };

  // Enhanced Reply Dialog
  const showReplyDialog = (submission) => {
    Alert.alert(
      'Add Transaction Details',
      `Submission: ${submission.text.substring(0, 50)}...

Enter your transaction details:`,
      [
        {
          text: 'Cancel',
          style: 'cancel'
        },
        {
          text: 'Enter Details',
          onPress: () => {
            Alert.prompt(
              'Transaction ID',
              'Enter your transaction ID:',
              [
                {
                  text: 'Cancel',
                  style: 'cancel'
                },
                {
                  text: 'Next',
                  onPress: (transactionId) => {
                    if (transactionId && transactionId.trim()) {
                      Alert.prompt(
                        'Amount',
                        'Enter the transaction amount:',
                        [
                          {
                            text: 'Cancel',
                            style: 'cancel'
                          },
                          {
                            text: 'Submit',
                            onPress: (amount) => {
                              if (amount && !isNaN(parseFloat(amount))) {
                                if (submission && submission._id) {
                                  handleReplyToSubmission(submission._id, transactionId.trim(), amount);
                                } else {
                                  Alert.alert('Error', 'Submission data not available.');
                                }
                              } else {
                                Alert.alert('Error', 'Please enter a valid amount.');
                              }
                            }
                          }
                        ],
                        'plain-text',
                        '200.50'
                      );
                    } else {
                      Alert.alert('Error', 'Please enter a valid transaction ID.');
                    }
                  }
                }
              ],
              'plain-text',
              'TX123456789'
            );
          }
        }
      ]
    );
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    setShowPaymentDetails(false);
    setShowPaymentForm(false);
    setAmount('');
    setTransactionId('');
    setPaymentScreenshot(null);
    setSelectedMethod(null);
    navigation.goBack();
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      {/* Header */}
      <View style={[styles.header, { backgroundColor: colors.cardBackground }]}>
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <Icon name="arrow-back" size={26} color={colors.text} />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: colors.text }]}>Deposit</Text>
        <TouchableOpacity onPress={() => setShowSubmissions(!showSubmissions)}>
          <Icon name={showSubmissions ? "wallet-outline" : "list-outline"} size={26} color={colors.primary} />
        </TouchableOpacity>
      </View>

      {/* Main UI */}
      {showSubmissions ? (
        <ScrollView showsVerticalScrollIndicator={false}>
          <View style={styles.submissionsHeader}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>Your Submissions</Text>
            <TouchableOpacity onPress={fetchSubmissions} disabled={loadingSubmissions}>
              {loadingSubmissions ? (
                <ActivityIndicator size="small" color={colors.primary} />
              ) : (
                <Icon name="refresh" size={20} color={colors.primary} />
              )}
            </TouchableOpacity>
          </View>

          {/* Submissions List */}
          {submissions.length > 0 ? (
            submissions.map((submission, index) => (
              <View key={submission._id} style={[styles.submissionCard, { backgroundColor: colors.cardBackground }]}>
                <View style={styles.submissionHeader}>
                  <Text style={[styles.submissionText, { color: colors.text }]} numberOfLines={2}>
                    {submission.text}
                  </Text>
                  <View style={[
                    styles.statusBadge,
                    { backgroundColor: submission.status === 'APPROVED' ? colors.success : 
                                     submission.status === 'PENDING' ? colors.warning : colors.error }
                  ]}>
                    <Text style={styles.statusText}>{submission.status}</Text>
                  </View>
                </View>
                
                {submission.imagePath && (
                  <TouchableOpacity 
                    style={styles.imageContainer}
                    onPress={() => {
                      Alert.alert(
                        'Payment Screenshot', 
                        'View full image?',
                        [
                          { text: 'Cancel' },
                          { text: 'View', onPress: () => {
                            console.log('Image URL:', `https://a77.onetradebot.com${submission.imagePath}`);
                          }}
                        ]
                      );
                    }}
                  >
                    <Image 
                      source={{ uri: `https://a77.onetradebot.com${submission.imagePath}` }} 
                      style={styles.submissionImage}
                      resizeMode="cover"
                    />
                    <View style={styles.imageOverlay}>
                      <Icon name="eye" size={16} color="#fff" />
                    </View>
                  </TouchableOpacity>
                )}
                
                <View style={styles.submissionFooter}>
                  <Text style={[styles.dateText, { color: colors.textSecondary }]}>
                    {new Date(submission.createdAt).toLocaleDateString('en-IN', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </Text>
                  {submission.reviewedAt && (
                    <Text style={[styles.reviewText, { color: colors.textSecondary }]}>
                      Reviewed: {new Date(submission.reviewedAt).toLocaleDateString('en-IN')}
                    </Text>
                  )}
                  
                  {/* Add Reply Button for pending submissions */}
                  {submission.status === 'PENDING' && (
                    <TouchableOpacity
                      style={[styles.replyButton, { backgroundColor: colors.primary }]}
                      onPress={() => showReplyDialog(submission)}
                    >
                      <Text style={styles.replyButtonText}>Add Details</Text>
                    </TouchableOpacity>
                  )}
                </View>
              </View>
            ))
          ) : (
            <View style={styles.emptyState}>
              <Icon name="document-text-outline" size={50} color={colors.textSecondary} />
              <Text style={[styles.emptyText, { color: colors.textSecondary }]}>No submissions found</Text>
              <Text style={[styles.emptySubtext, { color: colors.textSecondary }]}>Your deposit requests will appear here</Text>
            </View>
          )}

          <View style={{ height: 100 }} />
        </ScrollView>
      ) : showPaymentDetails ? (
        <ScrollView showsVerticalScrollIndicator={false}>

          <Text style={[styles.sectionTitle, { color: colors.text }]}>Payment Details</Text>

          {/* Plan Details (when coming from subscription) */}
          {planDetails && (
            <View style={[styles.planDetailsCard, { backgroundColor: colors.cardBackground, borderColor: colors.border }]}>
              <View style={styles.planHeader}>
                <View style={[styles.planIcon, { backgroundColor: `${colors.primary}20` }]}>
                  <Icon name="card" size={20} color={colors.primary} />
                </View>
                <View style={styles.planInfo}>
                  <Text style={[styles.planTitle, { color: colors.text }]}>{planDetails.title}</Text>
                  {planDetails.commission && (
                    <Text style={[styles.planCommission, { color: '#FFD700' }]}>
                      {planDetails.commission}% commission rate
                    </Text>
                  )}
                </View>
              </View>
              <View style={[styles.planAmountBadge, { backgroundColor: `${colors.primary}15` }]}>
                <Text style={[styles.planAmount, { color: colors.primary }]}>${amount}</Text>
              </View>
            </View>
          )}

          {/* Amount Input in Payment Details */}
          <View style={[styles.inputCard, { backgroundColor: colors.cardBackground }]}>
            <Text style={[styles.label, { color: colors.textSecondary }]}>Amount (USD)</Text>
            <View style={styles.inputRow}>
              <Text style={[styles.dollar, { color: colors.primary }]}>$</Text>
              <TextInput
                style={[styles.input, { color: colors.text }, planDetails && { opacity: 0.6 }]}
                placeholder="0.00"
                placeholderTextColor={colors.textSecondary}
                keyboardType="numeric"
                value={amount}
                editable={!planDetails}
                onChangeText={(newAmount) => {
                  if (!planDetails) { // Only allow editing if not from a plan
                    setAmount(newAmount);
                    // Note: Keeping the original QR code from API, not regenerating
                  }
                }}
              />
            </View>
            {planDetails && (
              <Text style={[styles.planIndicator, { color: colors.textSecondary }]}>
                Amount fixed for {planDetails.title}
              </Text>
            )}
          </View>

          {/* Amount Display */}
          <View style={[styles.paymentCard, { backgroundColor: colors.cardBackground }]}>
            <Text style={[styles.label, { color: colors.textSecondary }]}>Amount to Pay</Text>
            <Text style={[styles.amountText, { color: colors.success }]}>
              {selectedMethod?.name === 'Perfect Money' 
                ? `₹${amount ? (parseFloat(amount) * 95).toFixed(2) : '0.00'}` 
                : `$${amount || '0.00'}`}
            </Text>
          </View>

          {/* Single QR Code Display */}
          <View style={[styles.paymentCard, { backgroundColor: colors.cardBackground }]}>
            {/* Selected Method Info */}
            {selectedMethod && (
              <>
                <View style={styles.announcementHeader}>
                  <Icon name="megaphone" size={18} color={colors.primary} />
                  <Text style={[styles.announcementTitle, { color: colors.text }]}>{selectedMethod.name}</Text>
                </View>
                <Text style={[styles.announcementDesc, { color: colors.textSecondary }]}>
                  Selected payment method
                </Text>
              </>
            )}
            
            <Text style={[styles.label, { color: colors.textSecondary, marginTop: 8 }]}>UPI ID</Text>
            <View style={styles.upiRow}>
              <Text style={[styles.upiText, { color: colors.text }]}>{upiId}</Text>
              <TouchableOpacity onPress={() => {
                Clipboard.setString(upiId);
                Alert.alert('✅ Copied!', 'UPI ID copied to clipboard');
              }}>
                <Icon name="copy-outline" size={20} color={colors.primary} />
              </TouchableOpacity>
            </View>

            <Text style={[styles.label, { color: colors.textSecondary, marginTop: 16 }]}>QR Code</Text>
            <View style={[styles.apiIndicator, { backgroundColor: `${colors.success}15`, borderColor: colors.success }]}>
              <Icon name="checkmark-circle" size={14} color={colors.success} />
              <Text style={[styles.apiIndicatorText, { color: colors.success }]}>Official Payment QR</Text>
            </View>
            {qrCode && (
              <Image 
                source={{ uri: qrCode }} 
                style={styles.qr} 
                resizeMode="contain"
                onError={(error) => {
                  console.log('❌ QR Code Image Load Error:', error.nativeEvent.error);
                  console.log('Failed URL:', qrCode);
                }}
                onLoad={() => {
                  console.log('✅ QR Code Image Loaded Successfully');
                  console.log('Loaded URL:', qrCode);
                }}
              />
            )}
            
            <View style={styles.instructionBox}>
              <Icon name="information-circle" size={18} color={colors.warning} />
              <Text style={[styles.instructionText, { color: colors.textSecondary }]}>
                Scan QR code above, complete payment and upload screenshot below
              </Text>
            </View>
          </View>

          {/* Transaction ID Input */}
          <View style={[styles.inputCard, { backgroundColor: colors.cardBackground }]}>
            <Text style={[styles.label, { color: colors.textSecondary }]}>Transaction ID / UTR Number *</Text>
            <TextInput
              style={[styles.inputField, { color: colors.text, borderBottomColor: colors.border }]}
              placeholder="Enter transaction ID"
              placeholderTextColor={colors.textSecondary}
              value={transactionId}
              onChangeText={setTransactionId}
            />
          </View>

          {/* Screenshot Preview */}
          <View style={styles.uploadSection}>
            {paymentScreenshot ? (
              <View style={styles.screenshotBox}>
                <Image source={{ uri: paymentScreenshot.uri }} style={styles.screenshotImg} />
                <TouchableOpacity 
                  style={styles.changeImageBtn}
                  onPress={handleSelectPaymentScreenshot}
                >
                  <Icon name="camera" size={18} color="#fff" />
                  <Text style={styles.changeImageText}>Change</Text>
                </TouchableOpacity>
              </View>
            ) : (
              <TouchableOpacity
                style={[styles.uploadButton, { borderColor: colors.border }]}
                onPress={handleSelectPaymentScreenshot}
              >
                <Icon name="cloud-upload-outline" size={40} color={colors.primary} />
                <Text style={[styles.uploadBtnTxt, { color: colors.textSecondary }]}>
                  Upload Payment Screenshot *
                </Text>
              </TouchableOpacity>
            )}
          </View>

          {/* Submit Button */}
          <TouchableOpacity
            style={[styles.submitBtn, { backgroundColor: colors.primary }]}
            onPress={handleUploadPaymentProof}
          >
            {isLoading ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <Text style={styles.submitTxt}>Submit</Text>
            )}
          </TouchableOpacity>

          <View style={{ height: 100 }} />
        </ScrollView>
      ) : (
        <ScrollView showsVerticalScrollIndicator={false}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>Select Deposit Method</Text>

          {/* Deposit Cards */}
          {depositMethods.map((method) => (
            <TouchableOpacity
              key={method.id}
              style={[styles.methodCard, { backgroundColor: colors.cardBackground }]}
              onPress={() => handleMethodSelect(method)}
            >
              <View style={styles.methodRow}>
                <Text style={[styles.methodName, { color: method.color }]}>
                  {method.name}
                </Text>
                <Icon name="chevron-forward" size={22} color={colors.text} />
              </View>

              <View style={styles.methodInfo}>
                <Text style={[styles.infoText, { color: colors.textSecondary }]}>
                  Commission: {method.commissionValue}
                </Text>
                <Text style={[styles.infoText, { color: colors.textSecondary }]}>
                  Time: {method.avgTimeValue}
                </Text>
                <Text style={[styles.infoText, { color: colors.textSecondary }]}>
                  Limits: {method.limitsValue}
                </Text>
              </View>
            </TouchableOpacity>
          ))}

          <View style={{ height: 100 }} />
        </ScrollView>
      )}

      {/* Custom Success Modal */}
      <Modal
        visible={showSuccessModal}
        transparent={true}
        animationType="fade"
        onRequestClose={handleSuccessModalClose}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContainer, { backgroundColor: colors.cardBackground }]}>
            <View style={styles.modalHeader}>
              <View style={[styles.successIcon, { backgroundColor: '#4CAF5020' }]}>
                <Icon name="checkmark-circle" size={50} color="#4CAF50" />
              </View>
              <Text style={[styles.modalTitle, { color: colors.text }]}>Successfully Uploaded!</Text>
            </View>
            
            <View style={styles.modalContent}>
              <Text style={[styles.modalText, { color: colors.textSecondary }]}>
                Your deposit request has been submitted for admin approval.
              </Text>
              
              {successModalData && (
                <View style={styles.modalDetails}>
                  <View style={styles.modalDetailRow}>
                    <Text style={[styles.modalDetailLabel, { color: colors.textSecondary }]}>Amount:</Text>
                    <Text style={[styles.modalDetailValue, { color: colors.text }]}>${successModalData.amount}</Text>
                  </View>
                  <View style={styles.modalDetailRow}>
                    <Text style={[styles.modalDetailLabel, { color: colors.textSecondary }]}>Transaction ID:</Text>
                    <Text style={[styles.modalDetailValue, { color: colors.text }]}>{successModalData.transactionId}</Text>
                  </View>
                  <View style={styles.modalDetailRow}>
                    <Text style={[styles.modalDetailLabel, { color: colors.textSecondary }]}>Payment Method:</Text>
                    <Text style={[styles.modalDetailValue, { color: colors.text }]}>{successModalData.paymentMethod}</Text>
                  </View>
                </View>
              )}
              
              <Text style={[styles.modalSubtext, { color: colors.textSecondary }]}>
                Admin will review your transaction details and approve the deposit. Amount will be credited to your wallet after approval.
              </Text>
              
              <Text style={[styles.modalSubtext, { color: colors.textSecondary }]}>
                You can track the status in your submissions.
              </Text>
            </View>
            
            <TouchableOpacity
              style={[styles.modalButton, { backgroundColor: colors.primary }]}
              onPress={handleSuccessModalClose}
            >
              <Text style={styles.modalButtonText}>OK</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </View>
  );
};

/* -------------------- STYLES --------------------- */

const styles = StyleSheet.create({
  container: { flex: 1 },

  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 18,
    paddingTop: 45,
    borderBottomWidth: 1,
    borderColor: '#222',
  },

  headerTitle: {
    fontSize: 20,
    fontWeight: '700',
  },

  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginTop: 20,
    marginBottom: 12,
    marginLeft: 16,
  },

  methodCard: {
    marginHorizontal: 16,
    padding: 16,
    borderRadius: 16,
    marginBottom: 14,
    borderWidth: 1,
    borderColor: '#222',
    backgroundColor: 'rgba(255,255,255,0.05)',
  },

  methodRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },

  methodName: {
    fontSize: 18,
    fontWeight: '700',
  },

  methodInfo: { marginTop: 12 },
  infoText: { fontSize: 13, marginBottom: 4 },

  // Amount Card
  inputCard: {
    marginHorizontal: 16,
    padding: 16,
    borderRadius: 16,
    marginTop: 16,
  },

  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
  },

  dollar: { fontSize: 20, marginRight: 6, fontWeight: '600' },
  input: { fontSize: 20, flex: 1 },

  inputField: {
    fontSize: 16,
    paddingVertical: 10,
    borderBottomWidth: 1,
    marginTop: 8,
  },

  instructionBox: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 20,
    padding: 12,
    backgroundColor: 'rgba(255, 193, 7, 0.1)',
    borderRadius: 8,
    gap: 8,
  },

  instructionText: {
    fontSize: 13,
    flex: 1,
  },

  changeImageBtn: {
    position: 'absolute',
    bottom: 10,
    right: 10,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    gap: 4,
  },

  changeImageText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },

  // Payment Details
  paymentCard: {
    marginHorizontal: 16,
    padding: 20,
    borderRadius: 16,
    marginTop: 20,
  },

  label: { fontSize: 13, marginBottom: 4 },

  amountText: {
    fontSize: 22,
    fontWeight: '700',
  },

  upiRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 6,
  },

  upiText: {
    fontSize: 14,
    fontWeight: '500',
  },

  qr: {
    width: 200,
    height: 200,
    alignSelf: 'center',
    marginTop: 12,
  },

  uploadSection: { marginTop: 25, alignItems: 'center' },

  uploadButton: {
    borderWidth: 1,
    width: '85%',
    paddingVertical: 28,
    borderRadius: 14,
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.03)',
  },

  uploadBtnTxt: { marginTop: 10, fontSize: 13 },

  screenshotBox: {
    width: '85%',
    height: 200,
    borderRadius: 14,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#333',
  },

  screenshotImg: { width: '100%', height: '100%' },

  submitBtn: {
    marginHorizontal: 16,
    paddingVertical: 16,
    borderRadius: 14,
    marginTop: 20,
    alignItems: 'center',
  },

  submitTxt: {
    color: '#fff',
    fontSize: 17,
    fontWeight: '700',
  },

  // Submissions Styles
  submissionsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginHorizontal: 16,
    marginTop: 20,
    marginBottom: 12,
  },

  submissionCard: {
    marginHorizontal: 16,
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#222',
  },

  submissionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 10,
  },

  submissionText: {
    fontSize: 14,
    flex: 1,
    marginRight: 10,
    lineHeight: 20,
  },

  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    minWidth: 70,
    alignItems: 'center',
  },

  statusText: {
    color: '#fff',
    fontSize: 11,
    fontWeight: '600',
  },

  imageContainer: {
    position: 'relative',
    marginBottom: 10,
    alignSelf: 'flex-start',
  },

  submissionImage: {
    width: 80,
    height: 80,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#333',
  },

  imageOverlay: {
    position: 'absolute',
    top: 0,
    right: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    padding: 4,
    borderRadius: 4,
  },

  submissionFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },

  replyButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    marginTop: 4,
  },

  replyButtonText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },

  dateText: {
    fontSize: 12,
  },

  reviewText: {
    fontSize: 11,
    fontStyle: 'italic',
  },

  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
    paddingHorizontal: 20,
  },

  emptyText: {
    fontSize: 16,
    fontWeight: '600',
    marginTop: 16,
    textAlign: 'center',
  },

  emptySubtext: {
    fontSize: 13,
    marginTop: 4,
    textAlign: 'center',
  },

  // Plan Details Styles
  planDetailsCard: {
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 15,
  },

  planHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },

  planIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },

  planInfo: {
    flex: 1,
  },

  planTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 2,
  },

  planCommission: {
    fontSize: 13,
    fontWeight: '500',
  },

  planAmountBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
  },

  planAmount: {
    fontSize: 16,
    fontWeight: '700',
  },

  planIndicator: {
    fontSize: 12,
    fontStyle: 'italic',
    marginTop: 6,
    textAlign: 'center',
  },

  loadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 8,
    marginBottom: 10,
    gap: 8,
  },

  loadingText: {
    fontSize: 12,
  },

  infoBox: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 10,
    borderRadius: 8,
    borderWidth: 1,
    marginBottom: 12,
    gap: 8,
  },

  infoBoxText: {
    fontSize: 12,
    flex: 1,
  },

  apiIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    borderWidth: 1,
    marginTop: 8,
    marginBottom: 12,
    gap: 6,
  },

  apiIndicatorText: {
    fontSize: 11,
    fontWeight: '600',
  },

  announcementHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.1)',
  },

  announcementTitle: {
    fontSize: 16,
    fontWeight: '600',
    flex: 1,
  },

  announcementDesc: {
    fontSize: 13,
    lineHeight: 18,
    marginBottom: 16,
  },

  // Custom Modal Styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },

  modalContainer: {
    width: '100%',
    maxWidth: 400,
    borderRadius: 16,
    padding: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },

  modalHeader: {
    alignItems: 'center',
    marginBottom: 20,
  },

  successIcon: {
    width: 80,
    height: 80,
    borderRadius: 40,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },

  modalTitle: {
    fontSize: 20,
    fontWeight: '700',
    textAlign: 'center',
  },

  modalContent: {
    marginBottom: 24,
  },

  modalText: {
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'center',
    marginBottom: 16,
  },

  modalDetails: {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
  },

  modalDetailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.1)',
  },

  modalDetailLabel: {
    fontSize: 13,
    fontWeight: '500',
  },

  modalDetailValue: {
    fontSize: 13,
    fontWeight: '600',
  },

  modalSubtext: {
    fontSize: 12,
    lineHeight: 18,
    textAlign: 'center',
    marginTop: 8,
  },

  modalButton: {
    paddingVertical: 14,
    borderRadius: 10,
    alignItems: 'center',
  },

  modalButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default DepositScreen;
